import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Briefcase } from 'lucide-react'
import { trackEvent } from '../analytics'
import { API_BASE } from '../config'
import { SafeImage } from '../components/ui/SafeImage'

interface CaseStudyItem {
  id: number
  title: string
  slug: string
  summary?: string
  cover?: string
  publishedAt?: string
  clientName?: string
  industry?: string
}

export default function CaseStudyPage() {
  const [items, setItems] = useState<CaseStudyItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/case-studies`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.caseStudies)) return
        setItems(data.caseStudies)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="min-h-screen pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="container-wide">
        <motion.div
          className="text-center mb-10 sm:mb-14"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">Results</span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary dark:text-white mb-3 sm:mb-4 mt-2">
            Case Studies
          </h1>
          <p className="section-sub max-w-2xl mx-auto">
            Real outcomes from structured brand presence, lead generation, and sales systems — for B2B, real estate, marble & high-value sectors.
          </p>
        </motion.div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="card-glass p-5 sm:p-6 h-56 rounded-2xl animate-pulse bg-slate-200/30 dark:bg-slate-700/20"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="card-glass rounded-card p-8 sm:p-12 text-center max-w-2xl mx-auto">
            <Briefcase className="mx-auto text-primary dark:text-accent" size={48} strokeWidth={1.5} />
            <h2 className="mt-6 text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Stories coming soon</h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400">
              We&apos;re publishing detailed case studies. Meanwhile, book a call to discuss what we can build for your business.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 btn-primary"
              onClick={() => trackEvent('cta_click', { cta_name: 'discuss_project', from: 'case_study_empty' })}
            >
              Discuss your project
              <ArrowRight size={18} strokeWidth={2} />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {items.map((item, i) => (
              <Link key={item.id} to={`/case-study/${item.slug}`}>
                <motion.article
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  className="card-glass p-0 overflow-hidden hover:shadow-card-hover dark:hover:shadow-card-hover transition-all duration-300 group h-full flex flex-col"
                >
                  <div className="aspect-video overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <SafeImage
                      src={item.cover}
                      alt=""
                      label={item.title}
                      tone="default"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5 sm:p-6 flex-1 flex flex-col">
                    {(item.clientName || item.industry) && (
                      <p className="text-xs font-semibold text-accent uppercase tracking-wide mb-2">
                        {[item.clientName, item.industry].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white group-hover:text-primary dark:group-hover:text-accent transition-colors line-clamp-2">
                      {item.title}
                    </h2>
                    {item.summary && (
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-3 flex-1">{item.summary}</p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary dark:text-accent">
                      Read case study <ArrowRight size={16} />
                    </span>
                  </div>
                </motion.article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  )
}
